import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePBX, CALL_STATUS } from '../../context/PBXContext';
import Softphone from './Softphone';
import { Phone, X, PhoneIncoming } from 'lucide-react';

// Extract exerciseId from EMS exercise-related paths only (not MP)
const getExerciseIdFromPath = (pathname) => {
  // Skip Media Portal paths — phone should not appear on /mp/* pages
  if (pathname.startsWith('/mp/')) return null;
  const match = pathname.match(/\/(exercises|control-room|pbx-admin)\/([^/]+)/);
  return match ? match[2] : null;
};

export default function FloatingPhone() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const pbx = usePBX();

  const exerciseId = getExerciseIdFromPath(location.pathname);
  const isControlRoom = location.pathname.includes('/control-room/');

  // Only show on exercise pages — and NOT in the control room
  if (!exerciseId || isControlRoom) return null;

  const isInCall = pbx?.callStatus && pbx.callStatus !== CALL_STATUS.IDLE;
  const isIncoming = pbx?.callStatus === CALL_STATUS.INCOMING;

  return (
    <div className="fixed bottom-20 right-6 z-[1100] flex flex-col items-end gap-3" data-testid="floating-phone-widget">
      {/* Backdrop on mobile - tap to close */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 md:hidden z-[-1]"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Expanded softphone panel */}
      {isOpen && (
        <div
          className="w-[calc(100vw-3rem)] max-w-80 max-h-[60vh] overflow-y-auto shadow-2xl rounded-xl border bg-background"
          style={{ animation: 'slideInUp 0.2s ease-out' }}
        >
          <Softphone />
        </div>
      )}

      {/* Floating toggle button */}
      <button
        onClick={() => setIsOpen(o => !o)}
        data-testid="floating-phone-toggle"
        className={`
          w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-offset-2
          ${isOpen
            ? 'bg-red-500 hover:bg-red-600 focus:ring-red-400'
            : isIncoming
              ? 'bg-green-500 hover:bg-green-600 focus:ring-green-400 animate-pulse'
              : isInCall
                ? 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-400'
                : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
          }
        `}
        title={isOpen ? 'Close phone' : 'Open phone'}
      >
        {isOpen
          ? <X className="w-5 h-5 text-white" />
          : isIncoming
            ? <PhoneIncoming className="w-5 h-5 text-white" />
            : <Phone className="w-5 h-5 text-white" />
        }
        {/* Active call indicator dot */}
        {!isOpen && isInCall && (
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full border-2 border-background" />
        )}
      </button>

      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
